import { Component, Input, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonIcon,
  IonBadge,
  ModalController,
  ToastController
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addIcons } from 'ionicons';
import { close, copy, share, warningOutline } from 'ionicons/icons';
import { ConflictDeviceScan, TicketConflict } from '../models/server-sync.models';

@Component({
  selector: 'app-conflicts-modal',
  standalone: true,
  imports: [
    CommonModule,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonButton,
    IonIcon,
    IonBadge,
    TranslateModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-title>
          <ion-icon name="warning-outline"></ion-icon>
          {{ 'CONFLICTS.TITLE' | translate }}
        </ion-title>
        <ion-button slot="end" fill="clear" (click)="onDismiss()">
          <ion-icon name="close"></ion-icon>
        </ion-button>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding">
      <div class="summary-card">
        <div class="summary-copy">
          <div class="summary-label">{{ 'CONFLICTS.EVENT_LABEL' | translate }}</div>
          <div class="summary-title">{{ getDisplayedEventName() }}</div>
          <div class="summary-meta" *ngIf="shouldShowEventId()">
            {{ 'CONFLICTS.EVENT_ID_LABEL' | translate }}: {{ eventId }}
          </div>
        </div>
        <ion-badge color="warning">{{ conflicts.length || 0 }}</ion-badge>
      </div>

      <div class="conflicts-list" *ngIf="conflicts.length; else noConflicts">
        <div class="conflict-card" *ngFor="let conflict of conflicts; let index = index">
          <div class="conflict-topline">
            <div>
              <div class="ticket-label">{{ 'CONFLICTS.TICKET_LABEL' | translate }} {{ getTicketNumber(conflict) }}</div>
              <div class="ticket-id" *ngIf="shouldShowFullTicketId(conflict)">
                {{ conflict.ticketId }}
              </div>
            </div>
            <div class="conflict-index">#{{ index + 1 }}</div>
          </div>

          <div class="conflict-summary">{{ getConflictSummary(conflict) }}</div>

          <div class="detail-block">
            <div class="detail-label">{{ 'CONFLICTS.THIS_SCAN' | translate }}</div>
            <div class="detail-value">{{ getActorTitle(getCurrentScan(conflict)) }}</div>
            <div class="detail-meta">
              {{ getActionDescription(getCurrentScan(conflict)?.action) }}
            </div>
            <div class="detail-meta" *ngIf="getActorSubtitle(getCurrentScan(conflict)) as subtitle">
              {{ subtitle }}
            </div>
          </div>

          <div class="detail-block">
            <div class="detail-label">{{ 'CONFLICTS.CONFLICTS_WITH' | translate }}</div>
            <div class="detail-value">{{ getActorTitle(getOtherScan(conflict)) }}</div>
            <div class="detail-meta">
              {{ getActionDescription(getOtherScan(conflict)?.action) }}
            </div>
            <div class="detail-meta" *ngIf="getActorSubtitle(getOtherScan(conflict)) as subtitle">
              {{ subtitle }}
            </div>
          </div>

          <div class="reason-line">{{ getReasonLine(conflict) }}</div>
        </div>
      </div>

      <ng-template #noConflicts>
        <div class="empty-state">{{ 'CONFLICTS.EMPTY' | translate }}</div>
      </ng-template>

      <div class="actions">
        <ion-button expand="block" fill="outline" (click)="copyConflicts()">
          <ion-icon name="copy" slot="start"></ion-icon>
          {{ 'CONFLICTS.COPY' | translate }}
        </ion-button>
        <ion-button expand="block" (click)="shareConflicts()">
          <ion-icon name="share" slot="start"></ion-icon>
          {{ 'CONFLICTS.SHARE' | translate }}
        </ion-button>
      </div>
    </ion-content>
  `,
  styles: [`
    ion-title {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .summary-card,
    .conflict-card,
    .empty-state {
      border: 1px solid var(--ion-color-step-150, rgba(0, 0, 0, 0.08));
      border-radius: 14px;
      background: var(--ion-item-background, var(--ion-color-light));
    }
    .summary-card {
      padding: 14px 16px;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 16px;
    }
    .summary-label,
    .detail-label,
    .ticket-id,
    .detail-meta,
    .reason-line,
    .summary-meta {
      color: var(--ion-color-medium);
    }
    .summary-label,
    .detail-label,
    .conflict-index {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .summary-title,
    .ticket-label,
    .detail-value,
    .conflict-summary {
      color: var(--ion-text-color);
    }
    .summary-title,
    .ticket-label,
    .detail-value {
      font-weight: 600;
    }
    .summary-meta,
    .ticket-id,
    .detail-meta,
    .reason-line {
      font-size: 13px;
      line-height: 1.4;
    }
    .conflicts-list {
      display: grid;
      gap: 12px;
    }
    .conflict-card {
      padding: 14px 16px;
      display: grid;
      gap: 12px;
    }
    .conflict-topline {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
    }
    .conflict-index {
      font-weight: 700;
    }
    .conflict-summary {
      line-height: 1.45;
    }
    .detail-block {
      padding-top: 10px;
      border-top: 1px solid var(--ion-color-step-150, rgba(0, 0, 0, 0.08));
      display: grid;
      gap: 2px;
    }
    .reason-line {
      padding-top: 10px;
      border-top: 1px dashed var(--ion-color-step-150, rgba(0, 0, 0, 0.08));
    }
    .empty-state {
      padding: 16px;
      text-align: center;
      margin-bottom: 12px;
    }
    .actions {
      margin-top: 16px;
      display: grid;
      gap: 8px;
    }
  `]
})
export class ConflictsModalComponent {
  @Input() conflicts: TicketConflict[] = [];
  @Input() eventId = '';
  @Input() eventName = '';

  constructor(
    private modalController: ModalController,
    private toastController: ToastController,
    private translateService: TranslateService
  ) {
    addIcons({ close, copy, share, warningOutline });
  }

  onDismiss() {
    this.modalController.dismiss();
  }

  getDisplayedEventName(): string {
    return this.eventName?.trim() || this.eventId || this.t('CONFLICTS.UNKNOWN_EVENT');
  }

  shouldShowEventId(): boolean {
    return !!this.eventId && !!this.eventName && this.eventName !== this.eventId;
  }

  getTicketNumber(conflict: TicketConflict): string {
    const ticketId = conflict.ticketId || 'Unknown Ticket';
    const parts = ticketId.split('*');
    return parts.length >= 4 ? parts[3] : ticketId;
  }

  shouldShowFullTicketId(conflict: TicketConflict): boolean {
    return this.getTicketNumber(conflict) !== conflict.ticketId;
  }

  getCurrentScan(conflict: TicketConflict): ConflictDeviceScan | undefined {
    return conflict.thisDevice || this.buildFallbackScan(conflict.deviceId, conflict.action);
  }

  getOtherScan(conflict: TicketConflict): ConflictDeviceScan | undefined {
    return conflict.otherScans?.[0]
      || this.buildFallbackScan(conflict.conflictWithDevice, conflict.existingAction, conflict.existingTimestamp);
  }

  getActorTitle(scan: ConflictDeviceScan | undefined): string {
    if (!scan) {
      return this.t('CONFLICTS.UNKNOWN_DEVICE');
    }

    return scan.userDisplayName?.trim()
      || scan.deviceName?.trim()
      || scan.deviceId?.trim()
      || this.t('CONFLICTS.UNKNOWN_DEVICE');
  }

  getActorSubtitle(scan: ConflictDeviceScan | undefined): string {
    if (!scan) {
      return '';
    }

    const title = this.getActorTitle(scan);
    const deviceName = scan.deviceName?.trim();
    const deviceId = scan.deviceId?.trim();

    if (deviceName && deviceName !== title) {
      return deviceName;
    }

    if (deviceId && deviceId !== title && deviceId !== deviceName) {
      return deviceId;
    }

    return '';
  }

  getActionDescription(action: string | undefined): string {
    const normalized = this.normalizeAction(action);
    if (normalized === 'scan_in') {
      return this.t('CONFLICTS.ACTION_SCAN_IN');
    }
    if (normalized === 'scan_out') {
      return this.t('CONFLICTS.ACTION_SCAN_OUT');
    }
    return this.t('CONFLICTS.ACTION_UNKNOWN');
  }

  getConflictSummary(conflict: TicketConflict): string {
    const thisScan = this.getCurrentScan(conflict);
    const otherScan = this.getOtherScan(conflict);
    const thisActor = this.describeActor(thisScan);
    const otherActor = this.describeActor(otherScan);
    const thisAction = this.getActionPhrase(thisScan?.action);
    const otherAction = this.getActionPhrase(otherScan?.action);
    const actualIntervalSeconds = conflict.actualIntervalSeconds;

    switch (conflict.reasonCode) {
      case 'duplicate_entry':
        return this.t('CONFLICTS.SUMMARY_DUPLICATE_ENTRY', { actor: thisActor, otherActor });
      case 'exit_without_entry':
        return this.t('CONFLICTS.SUMMARY_EXIT_WITHOUT_ENTRY', { actor: thisActor });
      case 'already_exited':
        return this.t('CONFLICTS.SUMMARY_ALREADY_EXITED', { actor: thisActor, otherActor });
      case 'exit_too_soon':
        if (typeof actualIntervalSeconds === 'number') {
          return this.t('CONFLICTS.SUMMARY_EXIT_TOO_SOON', { actor: thisActor, actualIntervalSeconds });
        }
        return this.t('CONFLICTS.SUMMARY_EXIT_TOO_SOON_NO_INTERVAL', { actor: thisActor });
      default:
        break;
    }

    if (thisAction && otherAction && thisAction !== otherAction) {
      return this.t('CONFLICTS.SUMMARY_ACTION_MISMATCH', { actor: thisActor, otherActor, thisAction, otherAction });
    }

    if (thisAction && otherAction) {
      return this.t('CONFLICTS.SUMMARY_DUPLICATE_SCAN', { actor: thisActor, otherActor, thisAction });
    }

    return this.t('CONFLICTS.SUMMARY_GENERIC', { actor: thisActor, otherActor });
  }

  getReasonLine(conflict: TicketConflict): string {
    const translatedReason = this.getTranslatedReason(conflict);
    if (translatedReason) {
      return this.t('CONFLICTS.REASON_PREFIX', { reason: translatedReason });
    }

    const thisAction = this.normalizeAction(this.getCurrentScan(conflict)?.action);
    const otherAction = this.normalizeAction(this.getOtherScan(conflict)?.action);

    if (thisAction && otherAction && thisAction !== otherAction) {
      return this.t('CONFLICTS.REASON_PREFIX', {
        reason: this.t('CONFLICTS.REASON_ACTION_MISMATCH', {
          thisAction: this.getActionDescription(thisAction),
          otherAction: this.getActionDescription(otherAction)
        })
      });
    }

    return this.t('CONFLICTS.REASON_PREFIX', {
      reason: this.t('CONFLICTS.REASON_DUPLICATE_SCAN')
    });
  }

  private buildReportText(): string {
    const lines = [
      `${this.t('CONFLICTS.REPORT_TITLE')} (${this.conflicts.length})`,
      `${this.t('CONFLICTS.EVENT_LABEL')}: ${this.getDisplayedEventName()}`,
      ''
    ];

    if (this.shouldShowEventId()) {
      lines.splice(2, 0, `${this.t('CONFLICTS.EVENT_ID_LABEL')}: ${this.eventId}`);
    }

    this.conflicts.forEach((conflict, index) => {
      lines.push(
        `${index + 1}. ${this.t('CONFLICTS.TICKET_LABEL')} ${this.getTicketNumber(conflict)}`,
        `   ${this.getConflictSummary(conflict)}`,
        `   ${this.t('CONFLICTS.THIS_SCAN_LINE', {
          actor: this.describeActor(this.getCurrentScan(conflict)),
          action: this.getActionDescription(this.getCurrentScan(conflict)?.action)
        })}`,
        `   ${this.t('CONFLICTS.CONFLICTS_WITH_LINE', {
          actor: this.describeActor(this.getOtherScan(conflict)),
          action: this.getActionDescription(this.getOtherScan(conflict)?.action)
        })}`,
        `   ${this.getReasonLine(conflict)}`,
        ''
      );
    });

    return lines.join('\n');
  }

  async copyConflicts() {
    try {
      await navigator.clipboard.writeText(this.buildReportText());
      const toast = await this.toastController.create({
        message: this.t('CONFLICTS.COPY_SUCCESS'),
        duration: 2000,
        color: 'success'
      });
      await toast.present();
    } catch {
      const toast = await this.toastController.create({
        message: this.t('CONFLICTS.COPY_FAILED'),
        duration: 2000,
        color: 'danger'
      });
      await toast.present();
    }
  }

  async shareConflicts() {
    const reportText = this.buildReportText();
    if (navigator.share) {
      try {
        await navigator.share({
          title: this.t('CONFLICTS.SHARE_TITLE', { eventName: this.getDisplayedEventName() }),
          text: reportText
        });
        return;
      } catch {
        // fallback to copy
      }
    }
    await this.copyConflicts();
  }

  private buildFallbackScan(
    deviceId: string | undefined,
    action: string | undefined,
    timestamp?: number
  ): ConflictDeviceScan | undefined {
    if (!deviceId && !action && typeof timestamp !== 'number') {
      return undefined;
    }

    return {
      deviceId,
      action,
      timestamp
    };
  }

  private describeActor(scan: ConflictDeviceScan | undefined): string {
    if (!scan) {
      return this.t('CONFLICTS.UNKNOWN_DEVICE');
    }

    const title = this.getActorTitle(scan);
    const subtitle = this.getActorSubtitle(scan);
    return subtitle ? `${title} on ${subtitle}` : title;
  }

  private getActionPhrase(action: string | undefined): string | null {
    const normalized = this.normalizeAction(action);
    if (normalized === 'scan_in') {
      return 'in';
    }
    if (normalized === 'scan_out') {
      return 'out';
    }
    return null;
  }

  private normalizeAction(action: string | undefined): 'scan_in' | 'scan_out' | null {
    if (action === 'scan_in' || action === 'scan_out') {
      return action;
    }
    return null;
  }

  private getTranslatedReason(conflict: TicketConflict): string | null {
    switch (conflict.reasonCode) {
      case 'duplicate_entry':
        return this.t('CONFLICTS.REASON_DUPLICATE_ENTRY');
      case 'exit_without_entry':
        return this.t('CONFLICTS.REASON_EXIT_WITHOUT_ENTRY');
      case 'already_exited':
        return this.t('CONFLICTS.REASON_ALREADY_EXITED');
      case 'exit_too_soon':
        return this.t('CONFLICTS.REASON_EXIT_TOO_SOON', {
          minimumIntervalSeconds: conflict.minimumIntervalSeconds ?? 15,
          actualIntervalSeconds: conflict.actualIntervalSeconds ?? 0
        });
      case 'action_mismatch':
        return this.t('CONFLICTS.REASON_ACTION_MISMATCH', {
          thisAction: this.getActionDescription(this.getCurrentScan(conflict)?.action),
          otherAction: this.getActionDescription(this.getOtherScan(conflict)?.action)
        });
      case 'duplicate_scan':
        return this.t('CONFLICTS.REASON_DUPLICATE_SCAN');
      default:
        return conflict.reason || null;
    }
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translateService.instant(key, params);
  }
}
